import { useEffect, useState, useSyncExternalStore } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

type CommentThread = {
	id?: string | null;
	replies?: readonly ({ id?: string | null } | null)[] | null;
};

// Each mounted surface owns its registration, so closing one cannot clear another.
const openThreads = new Map<symbol, readonly string[]>();
const listeners = new Set<() => void>();
const emptySnapshot: ReadonlySet<string> = new Set();
let snapshot = emptySnapshot;

function publish(): void {
	snapshot = new Set([...openThreads.values()].flat());
	listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};

export function useOpenCommentConversationIds(): ReadonlySet<string> {
	return useSyncExternalStore(
		subscribe,
		() => snapshot,
		() => emptySnapshot,
	);
}

/** Suppress only conversations belonging to the thread currently open on this surface. */
export function useOpenCommentThread(
	comment: CommentThread | null | undefined,
	sessions: ReadonlyMap<string, { conversationId: string }> | null,
	isOpen: boolean,
): void {
	const [owner] = useState(() => Symbol('open-comment-thread'));
	const conversationIds =
		isOpen &&
		comment &&
		sessions &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? [comment, ...(comment.replies ?? [])].flatMap((item) => {
					const id = item?.id && sessions.get(item.id)?.conversationId;
					return id ? [id] : [];
				})
			: [];

	useEffect(() => {
		// Comment objects can be recreated on every render; publish only when IDs change.
		const previous = openThreads.get(owner) ?? [];
		if (
			previous.length === conversationIds.length &&
			previous.every((id, index) => id === conversationIds[index])
		)
			return;
		if (conversationIds.length) {
			openThreads.set(owner, conversationIds);
		} else {
			openThreads.delete(owner);
		}
		publish();
	});

	useEffect(
		() => () => {
			if (openThreads.delete(owner)) publish();
		},
		[owner],
	);
}

export function clearOpenCommentConversationsForTesting(): void {
	openThreads.clear();
	snapshot = emptySnapshot;
	listeners.clear();
}
